import { useState } from 'react';
import {
	useTranslationAdminDashboard,
	useTranslationDefault,
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Flex, Loader } from '@mantine/core';
import { ProfileBox } from '@dealstack/frontend/shared/components/ProfileBox/ProfileBox';
import { MiniListCard } from '@dealstack/frontend/shared/components/list/MiniListCard/MiniListCard';
import { InfoBoxRow } from '@dealstack/frontend/shared/components/InfoBoxRow/InfoBoxRow';
import { ProgrammeForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/ProgrammeForm';
import { StyledFormWrapper } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/programmeGeneralSettings.style';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import {
	getResourceTypePermissions,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getDealData } from '@dealstack/frontend/shared/dataExchange/services/project.service';
import {
	getMIPBankAccountData,
	updateMip as updateMipRequest,
	updateMIPBankAccountData as updateMIPBankAccountDataRequest,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	UPDATE_PROGRAMME_FORM_FIELDS,
	UpdateProgrammeSettingsInputsValues,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/ProgrammeGeneralSettings.types';
import { GenericError } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	useInviteProgrammeUser,
	useProgrammeUsers,
} from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { ProgrammeSettingsView } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.types';
import { StyledSettingsContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { ProgrammeEntitiesSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/ProgrammeEntitiesSection';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

interface Props {
	onViewChange: (view: ProgrammeSettingsView) => void;
}

export const ProgrammeGeneralSettings = ({ onViewChange }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tMipCreate = useTranslationMipCreate();
	const tDefault = useTranslationDefault();
	const { dealId, mipId: programmeId } = useRequiredParams('dealId', 'mipId');
	const tAdminDashboard = useTranslationAdminDashboard();
	const { value: multipleProgrammeEntitiesEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.MIP_MULTIPLE_PROGRAMME_ENTITY_UI
	);

	const { data: programme, isLoading: isProgrammeLoading } = useQuery({
		queryKey: [API_PATHS.DEAL_DATA_GET],

		queryFn: async () => {
			return getDealData(undefined);
		},

		select: (data) => data?.payload?.mips.find((mip) => mip.id === programmeId),
	});

	const queryClient = useQueryClient();
	const { mutateAsync: updateMip, isPending: isUpdateMipLoading } = useMutation({
		mutationFn: (variables: Parameters<typeof updateMipRequest>[0]) =>
			updateMipRequest(variables),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: [API_PATHS.DEAL_DATA_GET],
			}),
	});
	const { mutateAsync: updateMIPBankAccountDataData, isPending: isUpdateMIPBankDataLoading } =
		useMutation({
			mutationFn: (variables: Parameters<typeof updateMIPBankAccountDataRequest>[0]) =>
				updateMIPBankAccountDataRequest(variables),
			onSuccess: () =>
				queryClient.invalidateQueries({
					queryKey: [API_PATHS.MIP_BANK_ACCOUNT_GET],
				}),
		});

	const { data: bankAccountDetails, isLoading: isBankAccountLoading } = useQuery({
		queryKey: [API_PATHS.MIP_BANK_ACCOUNT_GET, programme?.id],
		queryFn: () => getMIPBankAccountData(),
		enabled: !!programme?.id,
		select: (res) => (res.payload?.id ? res.payload : undefined),
	});

	const handleUpdateProgramme = async (
		formData: UpdateProgrammeSettingsInputsValues,
		bankAccountId?: string
	) => {
		await Promise.all([
			updateMip({
				programmeName: formData[UPDATE_PROGRAMME_FORM_FIELDS.PROGRAMME_NAME],
				currency: formData[UPDATE_PROGRAMME_FORM_FIELDS.CURRENCY],
			}),
			...(bankAccountId
				? [
						updateMIPBankAccountDataData({
							body: {
								investorInstructions:
									formData[UPDATE_PROGRAMME_FORM_FIELDS.INVESTOR_INSTRUCTIONS],
							},
							params: { bankAccountId },
						}),
					]
				: []),
		])
			.then(() => {
				showToastV2({
					title: tDefault('settings_updated'),
					state: 'success',
				});
			})
			.catch(() => {
				showToastV2({
					title: tDefault('settings_updated_failed'),
					state: 'error',
				});
			});
	};
	const { mutate: inviteProgrammeUser, isPending } = useInviteProgrammeUser();

	const handleProgrammeInvite = ({ firstName, lastName, email, role }, { onSuccess }) => {
		if (!programme) {
			throw Error('Expect programme to be defined');
		}
		inviteProgrammeUser(
			{
				programmeId: programme.id,
				firstName,
				lastName,
				email,
				permissionRoles: [role],
				isContactPerson: false,
			},
			{
				onSuccess: async () => {
					await refetchProgrammeUsers();
					onSuccess?.();
				},
			}
		);
	};
	const [showInviteDrawer, setShowInviteDrawer] = useState(false);

	const {
		data: programmeUsers,
		isLoading: isLoadingProgrammeUsers,
		refetch: refetchProgrammeUsers,
	} = useProgrammeUsers({
		programmeId,
	});

	const mappedUsers =
		programmeUsers?.map((user) => ({
			id: user.id,
			value: {
				top: formatUserFullName(user),
				bottom: user.email,
			},
		})) ?? [];

	const programmePermissions = getResourceTypePermissions(ResourceType.PROGRAMME);
	const permissions = programmePermissions.map((permission) => ({
		label: tMipCreate(`invite_team_member.relation.${permission}`),
		value: permission,
	}));

	if (isProgrammeLoading || isLoadingProgrammeUsers || isBankAccountLoading) {
		return <Loader style={{ margin: 'auto' }} />;
	}

	if (!programme) {
		return <GenericError />;
	}

	return (
		<StyledSettingsContainer>
			<Flex
				sx={(theme) => ({
					flex: 1,
					border: `1px solid ${theme.colors.gray[2]}`,
					borderRadius: theme.radius.sm,
				})}
			>
				<StyledFormWrapper align="stretch" direction="column" gap="1rem" p="1rem">
					<ProgrammeForm
						programme={programme}
						bankAccountDetails={bankAccountDetails}
						showEntityField={!multipleProgrammeEntitiesEnabled}
						isLoading={
							isProgrammeLoading || isUpdateMipLoading || isUpdateMIPBankDataLoading
						}
						handleUpdateProgramme={handleUpdateProgramme}
					/>
					{multipleProgrammeEntitiesEnabled && dealId && programmeId && (
						<ProgrammeEntitiesSection dealId={dealId} programmeId={programmeId} />
					)}
				</StyledFormWrapper>
				<Flex direction="column" p="1rem" gap="1rem" w="33%" h="100%">
					<ProfileBox
						text={
							<Flex direction="column" gap="1rem" sx={{ overflow: 'hidden' }}>
								<InfoBoxRow
									title={tMipManage('settings_modal.programme_name')}
									value={programme?.programmeName}
								/>
								<InfoBoxRow
									title={tMipManage('settings_modal.entity_name')}
									value={programme?.entity?.displayName}
								/>
							</Flex>
						}
					/>
					<MiniListCard
						title={tMipManage('settings_modal.programme_team')}
						items={mappedUsers}
						children={
							<Button
								variant="secondary"
								mt="1rem"
								ml="0.5rem"
								onClick={() => setShowInviteDrawer(true)}
							>
								{tAdminDashboard('deal_settings.buttons.invite_team_member')}
							</Button>
						}
						moreItemsClick={() => onViewChange(ProgrammeSettingsView.Members)}
					/>
				</Flex>
			</Flex>
			{programme?.id && (
				<InviteTeamMemberDrawer
					isOpen={showInviteDrawer}
					setIsOpen={setShowInviteDrawer}
					onInvite={handleProgrammeInvite}
					isLoading={isPending}
					permissions={permissions}
					description={tMipManage('invite_member_drawer.description')}
					roleLabel={tMipManage('invite_member_drawer.form.role_field.label')}
				/>
			)}
		</StyledSettingsContainer>
	);
};
